import React from 'react';
import { Redirect, RouteProps } from 'react-router';
import { APP_ROUTES, IRoute } from './AppRoutes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { USER_ROLES, USER_STATUS } from '../utils/types/user';
import { getUserHomePage } from '../utils/helpers';
import { BaseRoute } from './BaseRoute';
import { useSnackbar } from 'notistack';
import { logout } from '../store/auth/slice';

type PPrivateRoute = RouteProps & IRoute;
export const PrivateRoute: React.FC<PPrivateRoute> = (props) => {
  const {
    component,
    path,
    roles = [],
    userStatuses,
    routes,
    ...otherProps
  } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { enqueueSnackbar } = useSnackbar();

  if (user) {
    const isUserStatusAccess =
      !userStatuses || userStatuses?.includes(user.status);

    const isDeletedUser = user.status === USER_STATUS.WILL_BE_DELETED;
    const mustPay = user.status === USER_STATUS.MUST_PAY;
    const waitForApproving = user.status === USER_STATUS.WAIT_FOR_APPROVING;
    const userIsWaitingForPayment =
      user.status === USER_STATUS.PAYMENT_IN_PROCESS;
    const isRolePermissionEnable = roles?.includes(user.role);

    const redirectTo = getUserHomePage(user);

    const hasNotAccessWaitForApproving =
      waitForApproving &&
      !userStatuses?.includes(USER_STATUS.WAIT_FOR_APPROVING);

    const hasNotAccessDeletedUser =
      isDeletedUser && !userStatuses?.includes(USER_STATUS.WILL_BE_DELETED);

    const lockedByAdmin = user.status === USER_STATUS.LOCKED_BY_ADMIN;

    const hasNotAccessPayUser =
      mustPay && !userStatuses?.includes(USER_STATUS.MUST_PAY);

    const waitForPaymentTransactionEnd =
      userIsWaitingForPayment &&
      !userStatuses?.includes(USER_STATUS.PAYMENT_IN_PROCESS);

    if (hasNotAccessWaitForApproving) {
      enqueueSnackbar(
        'You can’t use any of the features until you have been approved. The approval process usually takes up to 24 hours.',
        {
          variant: 'warning',
        },
      );
    }

    if (lockedByAdmin) {
      dispatch(logout());
      enqueueSnackbar('Your account was locked by admin.', {
        variant: 'warning',
      });
      return <Redirect to={APP_ROUTES.MAIN} />;
    }

    if (hasNotAccessDeletedUser) {
      enqueueSnackbar(
        'You don`t have access to this page. Please, restore your account before.',
        { variant: 'warning' },
      );
    }

    if (hasNotAccessPayUser) {
      enqueueSnackbar(
        'You don’t have access to this page. Please pay for your account first.',
        { variant: 'warning' },
      );
    }

    if (waitForPaymentTransactionEnd) {
      enqueueSnackbar(
        'You don`t have access to this page. Please, wait for successfully executed transaction.',
        { variant: 'warning' },
      );
    }

    if (!isRolePermissionEnable || !isUserStatusAccess) {
      return <Redirect to={redirectTo} />;
    }

    // We use RouteSuspense, then our component must be imported in async way
    return (
      <BaseRoute
        path={path}
        component={component}
        routes={routes}
        {...otherProps}
      />
    );
  }

  return <Redirect to={APP_ROUTES.MAIN} />;
};
